/*
 * Purpose: Passwordless sign-in — magic link, OTP, passkey, GitHub
 * Last modified: 2026-07-10 | Agent
 * Completeness: 96
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AUTH_CONFIG, authUrl } from '@/lib/auth-config';
import { authFetchErrorMessage } from '@/lib/apiBase';
import { bootstrapAuthSession } from '@/lib/authCompleteBootstrap';
import { resolveTenantsForEmail } from '@/lib/auth-tenants';
import { useAuthStore } from '@/stores/authStore';
import {
  AuthButton,
  AuthButtonOutline,
  AuthCard,
  AuthEmailContextBanner,
  AuthInput,
  AuthLabel,
  AuthMethodTabs,
  isAuthEmailValid,
  type AuthSignInMethod,
} from '@/components/auth/ui/AuthUi';
import GitHubSignInButton from '@/components/auth/GitHubSignInButton';
import { TenantPickerPanel } from '@/components/auth/TenantPickerPanel';
import { ContextualError } from '@/components/errors/ContextualError';

const ALL_METHODS: { id: AuthSignInMethod; label: string }[] = [
  { id: 'magic_link', label: 'Magic link' },
  { id: 'email_otp', label: 'Email OTP' },
  { id: 'passkey', label: 'Passkey' },
];

const METHODS = ALL_METHODS.filter((m) => AUTH_CONFIG.enabledMethods.includes(m.id));

function resetDeliveryState(
  setOtpSent: (v: boolean) => void,
  setMagicSent: (v: boolean) => void,
  setSentToEmail: (v: string) => void,
  setOtp: (v: string) => void,
) {
  setOtpSent(false);
  setMagicSent(false);
  setSentToEmail('');
  setOtp('');
}

export function PasswordlessSignInPanel({
  next = '/dashboard',
  onSuccess,
}: {
  next?: string;
  onSuccess?: () => void;
}) {
  const authSignIn = useAuthStore((s) => s.signIn);
  const hydrate = useAuthStore((s) => s.hydrate);
  const tenantPickRequired = useAuthStore((s) => s.tenantPickRequired);

  const [method, setMethod] = useState<AuthSignInMethod>(METHODS[0]?.id ?? 'magic_link');
  const [email, setEmail] = useState('');
  const [sentToEmail, setSentToEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [signedInPendingTenant, setSignedInPendingTenant] = useState(false);

  const emailLocked = Boolean(sentToEmail) && (otpSent || magicSent);
  const displayEmail = sentToEmail || email;
  const emailValid = isAuthEmailValid(displayEmail);

  function finish() {
    if (onSuccess) {
      onSuccess();
      return;
    }
    window.location.href = next;
  }

  function completeSignIn(userEmail: string) {
    const tenants = resolveTenantsForEmail(userEmail);
    authSignIn(userEmail, {
      tenants,
      activeTenantId: tenants.length === 1 ? tenants[0].id : undefined,
    });
    if (tenants.length > 1) {
      setSignedInPendingTenant(true);
      setMessage('Choose a workspace to continue.');
      return;
    }
    finish();
  }

  function changeEmail() {
    resetDeliveryState(setOtpSent, setMagicSent, setSentToEmail, setOtp);
    setMessage('');
    setError('');
  }

  function switchMethod(nextMethod: AuthSignInMethod) {
    setMethod(nextMethod);
    changeEmail();
  }

  async function postJson(path: string, body: Record<string, unknown>) {
    if (AUTH_CONFIG.mock) {
      completeSignIn(String(body.email || 'demo@autoblocks.run'));
      if (resolveTenantsForEmail(String(body.email)).length <= 1) {
        setMessage('Signed in locally (mock mode).');
      }
      return;
    }
    const res = await fetch(authUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      cache: 'no-store',
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
    return json;
  }

  async function sendMagicOrLink(path: string) {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await postJson(path, { email, next });
      if (!AUTH_CONFIG.mock) {
        setSentToEmail(email.trim());
        setMagicSent(true);
        setMessage('Check your inbox — the link expires in a few minutes.');
      }
    } catch (e) {
      setError(authFetchErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function sendOtp() {
    setBusy(true);
    setError('');
    try {
      await postJson(AUTH_CONFIG.endpoints.emailOtpSend, { email });
      if (!AUTH_CONFIG.mock) {
        setSentToEmail(email.trim());
        setOtpSent(true);
        setMessage('Enter the 6-digit code from your email.');
      }
    } catch (e) {
      setError(authFetchErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp() {
    setBusy(true);
    setError('');
    try {
      const verifyEmail = sentToEmail || email;
      if (AUTH_CONFIG.mock) {
        completeSignIn(verifyEmail);
        return;
      }
      await postJson(AUTH_CONFIG.endpoints.emailOtpVerify, { email: verifyEmail, code: otp });
      const boot = await bootstrapAuthSession(hydrate, useAuthStore.getState);
      if (!boot.authed) {
        throw new Error('OTP verified but session did not sync — try again.');
      }
      if (boot.tenantPickRequired) {
        setSignedInPendingTenant(true);
        setMessage('Choose a workspace to continue.');
        return;
      }
      finish();
    } catch (e) {
      setError(authFetchErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function signInWithPasskey() {
    setBusy(true);
    setError('');
    try {
      if (AUTH_CONFIG.mock) {
        completeSignIn(email || 'passkey@autoblocks.run');
        return;
      }
      setError('Passkey sign-in requires production auth API — use magic link or OTP for now.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Passkey failed');
    } finally {
      setBusy(false);
    }
  }

  if (signedInPendingTenant || tenantPickRequired) {
    return (
      <TenantPickerPanel
        next={next}
        onComplete={() => {
          setSignedInPendingTenant(false);
          finish();
        }}
      />
    );
  }

  const emailBannerVariant =
    magicSent ? 'magic_sent' : otpSent ? 'otp_sent' : method === 'passkey' && emailValid ? 'passkey' : 'ready';

  return (
    <AuthCard>
      <div className="text-center mb-2">
        <h1 className="text-xl font-semibold text-[var(--auth-fg)]">Sign in to {AUTH_CONFIG.brand}</h1>
        <p className="mt-1 text-sm text-[var(--auth-muted)]">
          Passwordless — GitHub, magic link, OTP, or passkey. Workspaces stay isolated per tenant.
        </p>
      </div>

      <GitHubSignInButton
        next={next}
        onSignedIn={() => {
          const tenants = resolveTenantsForEmail('github-user@autoblocks.run');
          if (tenants.length > 1) setSignedInPendingTenant(true);
          else finish();
        }}
      />

      <AuthMethodTabs methods={METHODS} value={method} onChange={switchMethod} />

      {method !== 'passkey' && (
        <>
          {!emailLocked ? (
            <label className="block">
              <AuthLabel>Work email</AuthLabel>
              <AuthInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="you@company.com"
              />
            </label>
          ) : null}
          {emailValid ? (
            <AuthEmailContextBanner
              email={displayEmail}
              variant={emailBannerVariant}
              onChangeEmail={emailLocked ? changeEmail : undefined}
            />
          ) : null}
        </>
      )}

      {method === 'magic_link' && (
        <>
          {!magicSent ? (
            <AuthButton disabled={busy || !emailValid} onClick={() => sendMagicOrLink(AUTH_CONFIG.endpoints.magicLink)}>
              {busy ? 'Sending…' : `Send magic link${emailValid ? '' : ''}`}
            </AuthButton>
          ) : (
            <AuthButtonOutline disabled={busy} onClick={() => sendMagicOrLink(AUTH_CONFIG.endpoints.magicLink)}>
              {busy ? 'Sending…' : 'Resend magic link'}
            </AuthButtonOutline>
          )}
          {!emailValid && !magicSent && (
            <p className="text-xs text-[var(--auth-muted)] text-center">Enter your work email above to enable.</p>
          )}
        </>
      )}

      {method === 'email_otp' && (
        <div className="space-y-3">
          {!otpSent ? (
            <AuthButton disabled={busy || !emailValid} onClick={sendOtp}>
              {busy ? 'Sending…' : 'Send OTP code'}
            </AuthButton>
          ) : (
            <>
              <AuthInput
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="text-center text-lg tracking-widest"
                aria-label={`6-digit code sent to ${displayEmail}`}
              />
              <AuthButton disabled={busy || otp.length !== 6} onClick={verifyOtp}>
                {busy ? 'Verifying…' : 'Verify & sign in'}
              </AuthButton>
              <AuthButtonOutline disabled={busy} onClick={sendOtp}>
                {busy ? 'Sending…' : 'Resend code'}
              </AuthButtonOutline>
            </>
          )}
        </div>
      )}

      {method === 'passkey' && (
        <div className="space-y-3">
          <label className="block">
            <AuthLabel>Email (optional for discoverable passkeys)</AuthLabel>
            <AuthInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@company.com"
            />
          </label>
          {emailValid ? (
            <AuthEmailContextBanner email={email} variant="passkey" />
          ) : (
            <p className="text-xs text-center text-[var(--auth-muted)]">
              Leave blank if your passkey is discoverable on this device.
            </p>
          )}
          <AuthButton disabled={busy} onClick={signInWithPasskey}>
            {busy ? 'Waiting…' : 'Sign in with passkey'}
          </AuthButton>
        </div>
      )}

      {message && (
        <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
          {message}
        </p>
      )}
      {error && (
        <ContextualError
          message={error}
          action={{ label: 'Try again', onClick: () => setError('') }}
          secondaryAction={{ label: 'Lost passkey?', href: AUTH_CONFIG.lostKeyPath }}
        />
      )}

      <p className="text-center text-xs text-muted-foreground">
        <Link to={AUTH_CONFIG.lostKeyPath} className="underline hover:text-foreground">
          Lost passkey or device?
        </Link>
      </p>
    </AuthCard>
  );
}

export default PasswordlessSignInPanel;
